import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useDashboard } from '@/api/hooks/useDashboard'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Users, AppWindow, Zap, Coins } from 'lucide-react'

const LINE_COLORS = [
  '#8752FF', '#3C19E9', '#06B6D4', '#10B981',
  '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899',
]

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function formatDate(d: string): string {
  const [, m, day] = d.split('-')
  return `${m}/${day}`
}

function formatExpiry(ts: number | null): string {
  if (!ts) return '永久'
  return new Date(ts * 1000).toLocaleDateString('zh-CN')
}

export default function Dashboard() {
  const { currentWorkspace } = useWorkspaceStore()
  const workspaceId = currentWorkspace?.id ? Number(currentWorkspace.id) : undefined
  const { data, isLoading, error } = useDashboard(workspaceId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        加载中...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        数据加载失败
      </div>
    )
  }

  const { summary, trends, appUsage } = data

  // Build recharts dataset: [{date, flavorA, flavorB, ...}]
  const allDates = trends[0]?.daily.map((d) => d.date) ?? []
  const chartData = allDates.map((date) => {
    const row: Record<string, string | number> = { date: formatDate(date) }
    trends.forEach((t) => {
      const found = t.daily.find((d) => d.date === date)
      row[t.flavorDisplayName] = found?.count ?? 0
    })
    return row
  })

  const summaryCards = [
    { label: '活跃应用', value: summary.activeApps, icon: <AppWindow size={22} /> },
    { label: '活跃设备', value: summary.activeDevices, icon: <Users size={22} /> },
    { label: '总调用次数', value: summary.totalCalls.toLocaleString(), icon: <Zap size={22} /> },
    { label: '剩余 Token', value: formatTokens(summary.totalRemainingTokens), icon: <Coins size={22} /> },
  ]

  return (
    <div className="w-full max-w-[1200px] mx-auto py-10 px-4 space-y-8">
      <h1 className="text-2xl font-bold text-gray-800">仪表盘</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-sm flex flex-col gap-2"
          >
            <div className="flex items-center gap-2 text-[#8752FF]">
              {card.icon}
              <span className="text-sm text-gray-500">{card.label}</span>
            </div>
            <span className="text-3xl font-bold text-gray-800">{card.value}</span>
          </div>
        ))}
      </div>

      {/* Trend chart */}
      {trends.length > 0 && (
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-gray-700 mb-4">近 14 天调用趋势</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9CA3AF' }} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              {trends.map((t, i) => (
                <Line
                  key={t.flavorId}
                  type="monotone"
                  dataKey={t.flavorDisplayName}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* App usage table */}
      <div className="rounded-xl border border-[#E5E7EB] bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="text-base font-semibold text-gray-700">应用授权详情</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {['应用名称', '已激活 / 上限', '已用 Token', '剩余 Token', '到期时间'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-gray-500 font-medium whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {appUsage.map((row) => (
                <tr key={row.flavorId} className="border-t border-[#F3F4F6] hover:bg-[#FAFAFA]">
                  <td className="px-5 py-3 text-gray-800 font-medium">{row.flavorDisplayName}</td>
                  <td className="px-5 py-3 text-gray-600">
                    {row.activatedCount}
                    <span className="text-gray-400"> / {row.maxDevices}</span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{formatTokens(row.usedTokens)}</td>
                  <td className="px-5 py-3 text-gray-600">{formatTokens(row.ableTokens)}</td>
                  <td className="px-5 py-3 text-gray-500">{formatExpiry(row.expiresAt)}</td>
                </tr>
              ))}
              {appUsage.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-gray-400">
                    暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
